import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/data-table/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/content/blog/")({ component: BlogIndex });

interface BlogRow {
  id: number;
  slug: string;
  title: string;
  author_id: number | null;
  status: "draft" | "published" | "archived";
  published_at: number | null;
  updated_at: number;
}

function BlogIndex() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["blog", "list"],
    queryFn: () => fetchAll<BlogRow>("/admin/blog"),
  });
  const publish = useMutation({ mutationFn: (id: number) => api.post(`/admin/blog/${id}/publish`),   onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["blog"] }); } });
  const unpublish = useMutation({ mutationFn: (id: number) => api.post(`/admin/blog/${id}/unpublish`), onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["blog"] }); } });
  const remove = useMutation({ mutationFn: (id: number) => api.del(`/admin/blog/${id}`),              onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["blog"] }); } });

  const columns: ColumnDef<BlogRow, unknown>[] = [
    {
      accessorKey: "title", header: "Title",
      cell: ({ row }) => (
        <Link to="/content/blog/$id" as never params={{ id: String(row.original.id) }} className="font-medium hover:text-primary">
          {row.original.title}
          <span className="ml-2 text-xs text-muted-foreground">/{row.original.slug}</span>
        </Link>
      ),
    },
    { id: "author", header: "Author", cell: ({ row }) => row.original.author_id ? <span>#{row.original.author_id}</span> : <span className="text-muted-foreground">—</span> },
    { accessorKey: "status", header: "Status", cell: ({ row }) => <StatusBadge status={row.original.status} />, filterFn: (r, _i, v) => r.getValue("status") === v },
    { accessorKey: "published_at", header: "Published", cell: ({ row }) => row.original.published_at ? format(row.original.published_at * 1000, "MMM d, yyyy") : <span className="text-muted-foreground">—</span> },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild><Link to="/content/blog/$id" as never params={{ id: String(row.original.id) }}>Edit</Link></DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.original.status === "published" ? (
              <DropdownMenuItem disabled={!can("blog", "publish")} onClick={() => unpublish.mutate(row.original.id)}><EyeOff className="h-3.5 w-3.5" /> Unpublish</DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled={!can("blog", "publish")} onClick={() => publish.mutate(row.original.id)}><Eye className="h-3.5 w-3.5" /> Publish</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!can("blog", "delete")}
              onClick={() => { if (confirm(`Delete "${row.original.title}"?`)) remove.mutate(row.original.id); }}
              className="text-destructive focus:text-destructive"
            ><Trash2 className="h-3.5 w-3.5" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Blog"
        description="Long-form posts. Each post belongs to its author."
        actions={can("blog", "create") && (
          <Link to="/content/blog/new" as never><Button><Plus className="h-4 w-4" /> New post</Button></Link>
        )}
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        filters={[{ id: "status", label: "Status", options: [
          { value: "draft", label: "Draft" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No posts yet."}
      />
    </div>
  );
}
